import { Link } from 'react-router-dom';
import { ShieldOff, ArrowLeft, LogOut } from 'lucide-react';
import type { Action, Module } from '@mygmao/shared';
import { useAuth } from '../contexts/AuthContext';

interface AccessDeniedPageProps {
  module?: Module;
  action?: Action;
}

/**
 * Page Accès refusé — US-TR-11.
 *
 * Rendue par `<ProtectedRoute>` quand un user authentifié tente une route
 * pour laquelle il n'a pas la permission requise. Différent de 401
 * (non authentifié, redirige vers `/login`).
 *
 * Si `<ProtectedRoute>` passe le `module`/`action` requis, la page affiche
 * en mode debug le code de permission manquant — utile pour comprendre quelle
 * permission demander à un admin.
 */
export function AccessDeniedPage({ module, action }: AccessDeniedPageProps = {}) {
  const { logout } = useAuth();
  const permissionCode = module && action ? `${module}.${action}` : null;
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4">
          <ShieldOff className="w-7 h-7 text-red-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">Accès refusé</h1>
        <p className="text-sm text-gray-600 mb-4">
          Vous n'avez pas la permission requise pour consulter cette page. Si vous pensez qu'il
          s'agit d'une erreur, contactez votre administrateur.
        </p>
        {permissionCode && (
          <p className="text-xs text-gray-500 mb-6">
            Permission manquante :{' '}
            <code className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-mono">
              {permissionCode}
            </code>
          </p>
        )}
        <div className="flex items-center justify-center gap-2">
          <Link
            to="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au tableau de bord
          </Link>
          <button
            onClick={() => logout()}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Se déconnecter
          </button>
        </div>
      </div>
    </div>
  );
}
